// pages/Simulate/useWorkingCopyRecovery.ts — drop 2 wiring
// ─────────────────────────────────────────────────────────────────────────
// THE RECOVERY LOOP. Write the draft as it changes, offer it back on open.
//
// `workingCopyStorage` is the pure half: keys, TTL, conflict rule, the
// sign-in question. This is the half that has a clock and a lifecycle.
//
// THREE THINGS IT GETS RIGHT THAT A NAIVE VERSION WOULD NOT.
//
// 1. IT WRITES ONLY WHEN DIRTY, AND DELETES WHEN CLEAN. A working copy
//    identical to the saved plan is not a recovery, it is noise, and
//    offering it back produces a restore notice for a plan nobody edited.
//    Committing or discarding clears the key rather than leaving a stale
//    copy that will be offered on the next open.
//
// 2. IT DEBOUNCES. localStorage.setItem is synchronous and the working copy
//    changes on every keystroke. Drop 1 took an 82 ms Monte Carlo off that
//    path; putting a serialise-and-write back on it would be the same
//    mistake in a smaller size. The write follows the edit by a beat.
//
// 3. IT OFFERS ONCE PER PLAN. Without a per-plan guard, dismissing a
//    restore and then editing re-reads the key and offers it again, which
//    reads as the product arguing with you.
//
// WHAT IT DOES NOT DO: the anonymous-to-signed-in migration. That belongs
// in SessionModeBridge, because people sign in from Dashboard and a
// migration that only fires on a Simulate visit orphans the key past the
// routing decision. `migrationQuestion` is exported from the storage module
// for that caller.
//
// Copy rule: no em or en dashes in any user-facing string.
// ─────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from 'react';
import type { LeverModel } from '../leverSchema';
import type { PlanQuestionInstance } from '../questionViews';
import {
	clearWorkingCopy,
	readWorkingCopy,
	restoreDecision,
	writeWorkingCopy,
	type KeyValueStore,
	type Namespace,
	type RestoreDecision,
	type WorkingCopyRecord,
} from '../workingCopyStorage';

/** Long enough that continuous typing never writes, short enough that a
 *  crash loses at most a beat of work. Deliberately the same figure as the
 *  deferral policy's idle window: they are the same judgement about when a
 *  person has stopped. */
const WRITE_DEBOUNCE_MS = 400;

export interface RecoveryInput {
	/** null in SSR or a locked-down browser. Every call tolerates it. */
	store: KeyValueStore | null;
	namespace: Namespace;
	/** The open plan, or null for the unsaved first-plan draft. */
	planId: string | null;
	planName: string;
	/** The saved plan's `_lastUpdated`, for the conflict check. */
	savedUpdatedAt: number | null;
	/** The live working copy. */
	workingLevers: readonly LeverModel[];
	/** Instances travel with the levers. A draft restored without them
	 *  brings back the moves and loses the view that explains them, which
	 *  reads as the question having been deleted. */
	workingQuestions: readonly PlanQuestionInstance[];
	workingTargetAge: number | null;
	/** From the diff. Zero means clean, and clean means no recovery. */
	changeCount: number;
	/** Suppresses everything. A demo plan is not worth recovering. */
	enabled: boolean;
}

export interface Recovery {
	/** What to offer, or none. Read once per plan; acting clears it. */
	offer: RestoreDecision;
	/** Adopt the draft. The caller passes the levers to
	 *  `workingPlan.restore` and the target through the lens. */
	acceptRestore: () => WorkingCopyRecord | null;
	/** Throw the draft away and keep what is saved. */
	dismissRestore: () => void;
	/** Call after a commit or a discard: the draft is no longer a
	 *  recovery, and leaving it means offering it again on the next open. */
	clear: () => void;
}

export function useWorkingCopyRecovery(input: RecoveryInput): Recovery {
	const {
		store,
		namespace,
		planId,
		planName,
		savedUpdatedAt,
		workingLevers,
		workingQuestions,
		workingTargetAge,
		changeCount,
		enabled,
	} = input;

	const [offer, setOffer] = useState<RestoreDecision>({ kind: 'none' });

	/** Which plan we have already read for. `undefined` means never. */
	const readFor = useRef<string | null | undefined>(undefined);
	/** The record behind the current offer, for acceptRestore. */
	const offered = useRef<WorkingCopyRecord | null>(null);

	// ── Read once per plan ──
	useEffect(() => {
		if (!enabled) return;
		if (readFor.current === planId) return;
		readFor.current = planId;

		const result = readWorkingCopy(store, planId, Date.now(), namespace);
		const decision = restoreDecision(result, savedUpdatedAt);
		offered.current = decision.kind === 'none' ? null : decision.record;
		setOffer(decision);
		// savedUpdatedAt is deliberately NOT a dependency. It changes on
		// every commit, and re-running this on commit would re-offer the
		// draft the commit just consumed. The guard is the plan id.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled, planId, store, namespace]);

	// ── Write, debounced, only while dirty ──
	useEffect(() => {
		if (!enabled) return;

		if (changeCount === 0) {
			// Clean is not a recovery. Clearing here is what stops a
			// discarded draft being offered back on the next open.
			clearWorkingCopy(store, planId, namespace);
			return;
		}

		const t = setTimeout(() => {
			const record: WorkingCopyRecord = {
				v: 1,
				ts: Date.now(),
				planId,
				forkedFromUpdatedAt: savedUpdatedAt,
				levers: workingLevers as unknown[],
				questionInstances: workingQuestions as unknown[],
				targetAge: workingTargetAge,
				changeCount,
				planName,
			};
			writeWorkingCopy(store, record, namespace);
		}, WRITE_DEBOUNCE_MS);

		return () => clearTimeout(t);
	}, [
		enabled,
		store,
		namespace,
		planId,
		planName,
		savedUpdatedAt,
		workingLevers,
		workingQuestions,
		workingTargetAge,
		changeCount,
	]);

	const acceptRestore = useCallback((): WorkingCopyRecord | null => {
		const record = offered.current;
		offered.current = null;
		setOffer({ kind: 'none' });
		return record;
	}, []);

	const dismissRestore = useCallback((): void => {
		offered.current = null;
		setOffer({ kind: 'none' });
		clearWorkingCopy(store, planId, namespace);
	}, [store, planId, namespace]);

	const clear = useCallback((): void => {
		clearWorkingCopy(store, planId, namespace);
	}, [store, planId, namespace]);

	return { offer, acceptRestore, dismissRestore, clear };
}

/** The browser's localStorage, or null where there is not one. Passed in
 *  rather than reached for, so the hook stays testable and a locked-down
 *  browser degrades instead of throwing. */
export function browserStore(): KeyValueStore | null {
	try {
		if (typeof window === 'undefined') return null;
		// Touching the API is the test: private mode can define it and
		// throw on use.
		window.localStorage.getItem('nisk.wc.probe');
		return window.localStorage;
	} catch {
		return null;
	}
}
